// =============================================================================
// Deleted Organizations - Organizzazioni eliminate ma ripristinabili
// =============================================================================
// Un'organizzazione eliminata sparisce dallo switcher: senza questo elenco
// l'owner che ha sbagliato non avrebbe modo di tornare indietro da solo.
// Compare sia nella scelta organizzazione sia nell'onboarding (chi elimina la
// sua unica organizzazione finisce li').
// =============================================================================

import { RotateCcw, Trash2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMyDeletedTenants, useRestoreTenant } from '../../api/hooks';
import { useTranslatedError } from '../../hooks/useTranslatedError';
import { useToast } from '../../store/toastStore';
import { formatDate } from '../../lib/utils';
import { OrgAvatar } from './OrgAvatar';

interface Props {
  /// Disattiva la query quando l'utente non e' (ancora) autenticato
  enabled?: boolean;
}

export function DeletedOrganizations({ enabled = true }: Props) {
  const { t } = useTranslation('auth');
  const { getErrorMessage } = useTranslatedError();
  const toast = useToast();

  const { data: deleted = [] } = useMyDeletedTenants(enabled);
  const restore = useRestoreTenant();

  if (deleted.length === 0) return null;

  const handleRestore = async (organizationId: string, name: string) => {
    try {
      await restore.mutateAsync(organizationId);
      toast.success(t('deletedOrgs.restored', { name }));
    } catch (err) {
      toast.error(t('deletedOrgs.restoreError'), getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-3 mb-6">
      <h2 className="text-sm font-medium text-gray-700 flex items-center gap-2">
        <Trash2 className="w-4 h-4 text-gray-400" />
        {t('deletedOrgs.title')}
      </h2>

      {deleted.map((org) => {
        const busy = restore.isPending && restore.variables === org.organizationId;
        return (
          <div
            key={org.organizationId}
            className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 flex items-center gap-3"
          >
            {/* Opacita' ridotta: e' un'organizzazione spenta, non una fra le altre */}
            <div className="opacity-50">
              <OrgAvatar branding={org.branding} name={org.tenantName} size="md" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-600 truncate">{org.tenantName}</h3>
              <p className="text-xs text-gray-500">
                {t('deletedOrgs.purgeOn', { date: formatDate(org.purgeAt) })}
              </p>
            </div>
            <button
              onClick={() => handleRestore(org.organizationId, org.tenantName)}
              disabled={restore.isPending}
              className="flex-shrink-0 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 inline-flex items-center gap-1.5 transition-colors"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4" />
              )}
              {t('deletedOrgs.restore')}
            </button>
          </div>
        );
      })}
    </div>
  );
}
